import { computed, onMounted, ref, type ComputedRef } from 'vue';
import { storeToRefs } from 'pinia';
import { useStore } from 'vuex';
import type { Geofence } from '@gpx-spa/geo-domain';
import { useDebouncedRef } from '@gpx-spa/ui';
import { getAccountGeofences } from '@gpx-spa/queries';
import {
  createGoogleRoutesRoutingProvider,
  createLeafletMapProvider,
} from '@gpx-spa/geo-adapters';
import {
  APP_MAP_TILE_OPTIONS,
  getAppMapTileAttribution,
  getAppMapTileUrl,
  isMapAttributionVisible,
} from '@/utils/app-map-tiles';
import { formatPathLabel } from '@/utils/shipment-route-labels';
import { useShipmentFormMapPaint } from '@/components/shipment/form/domain/useShipmentFormMapPaint';
import type { FormMiddleStopPaint } from '@/components/shipment/form/domain/map-paint-types';
import { useShipmentFormSelectedPaths } from '@/components/shipment/form/domain/useShipmentFormSelectedPaths';
import {
  COMPOSER_DEBOUNCE,
  COMPOSER_MAP_DEFAULT_CENTER,
  COMPOSER_STOP_RADIUS_DEFAULT_M,
  PATH_ALTERNATIVE_COLORS,
} from '@/components/shipment/form/domain/constants';
import { flattenRoutePlanPaths } from '@/components/shipment/form/domain/flatten-route-plan-paths';
import {
  getStopRoutingCoordinates,
  toResolvedPlaceForMap,
} from '@/components/shipment/form/utils/shipment-form-stop-coords';
import { useShipmentFormStore } from '@/stores/useShipmentFormStore';
import type { LatLng } from '@/components/shipment/form/utils/build-routing-coordinates';
import type { RoutingStrategy } from '@/components/shipment/form/utils/shipment-form-routing';
import { useShipmentFormRouting } from '@/composables/useShipmentFormRouting';
import type { ShipmentFormMapContext } from '@/components/shipment/form/types/shipment-form-page-context';

export function useShipmentFormMapScene(
  mapContext: ComputedRef<ShipmentFormMapContext>,
) {
  const vuexStore = useStore();
  const accountId = computed(
    () => vuexStore.getters['auth/accountId'] as number | undefined,
  );

  const { data: geofenceData, isLoading: geofencesLoading } =
    getAccountGeofences(accountId);

  const geofences = computed<Geofence[]>(() => {
    const raw = geofenceData.value;
    if (!raw || !Array.isArray(raw)) return [];
    return raw;
  });

  const mapProvider = createLeafletMapProvider({
    getRasterTileUrl: getAppMapTileUrl,
    getTileAttribution: getAppMapTileAttribution,
    tileLayerOptions: { ...APP_MAP_TILE_OPTIONS },
    defaultCenter: COMPOSER_MAP_DEFAULT_CENTER,
    defaultZoom: 9,
    showAttribution: isMapAttributionVisible(),
  });

  const googleRouting = createGoogleRoutesRoutingProvider({
    getApiKey: () =>
      (import.meta.env.VITE_GOOGLE_MAPS_API_KEY as string | undefined)?.trim() ??
      '',
  });

  const hasGoogleKey = computed(() =>
    Boolean(
      (import.meta.env.VITE_GOOGLE_MAPS_API_KEY as string | undefined)?.trim(),
    ),
  );

  const canCallRoutingApi = computed(
    () => Boolean(accountId.value) || hasGoogleKey.value,
  );
  const placesAutocompleteDisabled = computed(() => !hasGoogleKey.value);

  const formStore = useShipmentFormStore();
  const {
    stops,
    routePlan,
    corridorBufferM,
    geometryQualityPercent,
    geoCache,
  } = storeToRefs(formStore);

  const routingStrategy = computed(
    () => mapContext.value.routingStrategy ?? 'originToDestination',
  );

  const { routingCoordinates, canComputeRoute } = useShipmentFormRouting({
    strategy: routingStrategy,
    stops: () => stops.value,
    trackerPosition: () => mapContext.value.primaryTrackerPosition ?? null,
  });

  const mapReady = ref(false);
  const routingLoading = ref(false);
  const computingRoute = ref(false);
  const routeError = ref<string | null>(null);
  const routeWarning = ref<string | null>(null);

  const radiusStartM = computed(
    () => stops.value[0]?.radiusM ?? COMPOSER_STOP_RADIUS_DEFAULT_M,
  );
  const radiusEndM = computed(
    () =>
      stops.value[stops.value.length - 1]?.radiusM ??
      COMPOSER_STOP_RADIUS_DEFAULT_M,
  );

  const startResolved = computed(() => toResolvedPlaceForMap(stops.value[0]!));
  const endResolved = computed(() =>
    toResolvedPlaceForMap(stops.value[stops.value.length - 1]!),
  );

  const startGeofence = computed(() => stops.value[0]?.geofence ?? null);
  const endGeofence = computed(
    () => stops.value[stops.value.length - 1]?.geofence ?? null,
  );

  const formStopsPaint = computed<(FormMiddleStopPaint | null)[]>(() =>
    stops.value.map((s) => {
      const c = getStopRoutingCoordinates(s);
      if (!c) return null;
      return { coordinates: c, radiusM: s.radiusM, geofence: s.geofence ?? null };
    }),
  );

  const {
    debounced: corridorBufferMDebounced,
    flush: commitCorridorBufferImmediately,
  } = useDebouncedRef(corridorBufferM, COMPOSER_DEBOUNCE.corridorBufferMs);

  const {
    debounced: geometryQualityDebounced,
    flush: commitGeometryQualityImmediately,
  } = useDebouncedRef(
    geometryQualityPercent,
    COMPOSER_DEBOUNCE.geometryQualityMs,
  );

  const { selectedByPathId } = useShipmentFormSelectedPaths(
    routePlan,
    geoCache.value,
  );

  const flattenedPaths = computed(() => flattenRoutePlanPaths(routePlan.value));

  const pathUi = computed(() => {
    const out: Record<
      string,
      {
        color: string;
        label: string;
        providerLabel?: string;
        segmentTitle: string;
      }
    > = {};
    flattenedPaths.value.forEach((row, i) => {
      out[row.pathId] = {
        color:
          PATH_ALTERNATIVE_COLORS[i % PATH_ALTERNATIVE_COLORS.length] ?? '#333',
        label: formatPathLabel(row.path),
        ...(row.provider === 'learned'
          ? { providerLabel: 'Learned' }
          : {}),
        segmentTitle: `Stop ${row.fromStopIndex + 1} → Stop ${row.toStopIndex + 1}`,
      };
    });
    return out;
  });

  const pathIds = computed(() => flattenedPaths.value.map((row) => row.pathId));

  const pathGroups = computed(() => {
    const groups: Array<{ title: string; pathIds: string[] }> = [];
    for (const row of flattenedPaths.value) {
      const title = `Stop ${row.fromStopIndex + 1} → Stop ${row.toStopIndex + 1}`;
      const last = groups[groups.length - 1];
      if (last && last.title === title) {
        last.pathIds.push(row.pathId);
      } else {
        groups.push({ title, pathIds: [row.pathId] });
      }
    }
    return groups;
  });

  const showAlternativesSection = computed(
    () => pathIds.value.length > 0 || routingLoading.value,
  );

  const mapSceneReady = computed(() => {
    if (mapContext.value.isFormHydrated === false) return false;
    if (mapContext.value.routesHydrationReady === false) return false;
    if (geofencesLoading.value) return false;
    if (mapContext.value.trackerLoading === true) return false;
    return true;
  });

  const isMapBootstrapMode = computed(
    () => mapContext.value.isMapBootstrapMode === true,
  );

  const { mapWorkBusy, requestRouteOverview, onMapReady } =
    useShipmentFormMapPaint({
      mapProvider,
      mapReady,
      routePlan,
      selectedByPathId,
      corridorBufferMDebounced,
      geometryQualityDebounced,
      startResolved,
      endResolved,
      startGeofence,
      endGeofence,
      radiusStartM,
      radiusEndM,
      formStopsPaint,
      pathUi,
      computingRoute,
      routingLoading,
      geoCache: geoCache.value,
      trackerPositions: computed(
        () => mapContext.value.trackerPositions ?? [],
      ),
      cameraEnabled: mapSceneReady,
      mapBootstrapMode: isMapBootstrapMode,
    });

  const showMapBusyOverlay = computed(() => mapWorkBusy.value);
  const showAlternativesHeaderSpinner = computed(
    () => showAlternativesSection.value && mapWorkBusy.value,
  );
  const canCompute = computed(
    () => canCallRoutingApi.value && canComputeRoute.value,
  );

  function togglePath(pathId: string, checked: boolean) {
    selectedByPathId.value = { ...selectedByPathId.value, [pathId]: checked };
  }

  return {
    geofences,
    geofencesLoading,
    mapProvider,
    googleRouting,
    hasGoogleKey,
    accountId,
    placesAutocompleteDisabled,
    formStore,
    stops,
    routePlan,
    corridorBufferM,
    routingStrategy,
    routingCoordinates,
    mapReady,
    routingLoading,
    computingRoute,
    routeError,
    routeWarning,
    selectedByPathId,
    pathUi,
    pathIds,
    pathGroups,
    showAlternativesSection,
    showMapBusyOverlay,
    showAlternativesHeaderSpinner,
    canCompute,
    canCallRoutingApi,
    commitCorridorBufferImmediately,
    commitGeometryQualityImmediately,
    requestRouteOverview,
    onMapReady,
    togglePath,
    geometryQualityDebounced,
    corridorBufferMDebounced,
  };
}
